// ClientTrackDetailPanel — sticky right rail for the selected signal.
//
// Field paths match clients/{id}.json v1:
//   trackCards[].latestWeeklyFraming
//   trackCards[].market.{score, momentum, convictionLevel, maturityStage,
//                        currentMarketMovement, weeklyComparisonNarrative, evolution}
//   trackCards[].client.{relevanceScore, position, cardCategory, urgency,
//                        opportunityLevel, threatLevel, blindSpotLevel,
//                        whyRelevantToClient, opportunity, threat, blindSpot,
//                        recommendedNextAction}
//   trackCards[].affectedBusinessSegments[]
//   trackCards[].affectedCapabilityAreas[]
//   trackCards[].evidence.{potentialPartners, existingRelationshipsToLeverage,
//                          relevantCompetitors, urls, companies, companyTypes}

import React, { useState } from "react";
import {
    CATEGORY_LABEL,
    CATEGORY_TONE,
    hasContent,
    levelLabel,
    levelTone,
    extractEvidenceUrls,
    readMarketSignalContext,
    readClientRelevance,
    isPartnerAlsoCompetitor,
    engagementLabel,
    buildBlindSpotGroups,
    SEVERITY_TONE,
    relatedTrackIdsOf,
    isDuplicateRelevanceText,
    humanizeEnumLabel,
    marketMomentumLabel,
    marketMomentumTone,
    signalStatusLabel,
} from "../../../lib/clients";

const Badge = ({ tone = "muted", children }) => (
    <span className={`badge ${tone === "accent" ? "badge-accent" : tone === "positive" ? "badge-positive" : tone === "warning" ? "badge-warning" : tone === "negative" ? "badge-negative" : tone === "regulatory" ? "badge-regulatory" : tone === "ink" ? "badge-ink" : ""}`}>
        {children}
    </span>
);

const Pill = ({ label, value, tone, hidden }) => (
    !hidden ? (
        <div className="flex flex-col">
            <div className="eyebrow">{label}</div>
            <div className="mt-1.5"><Badge tone={tone}>{value}</Badge></div>
        </div>
    ) : null
);

const Section = ({ title, children, testId, hidden, collapsible = false, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    if (hidden) return null;
    if (!collapsible) {
        return (
            <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid={testId}>
                <div className="eyebrow mb-1.5">{title}</div>
                {children}
            </section>
        );
    }
    // Collapsible variant mirrors `MarketTrackDetailPanel`'s `Section`:
    // header is a full-width button with a `−` / `+` affordance, expanded
    // by default to preserve current visual hierarchy.
    return (
        <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid={testId}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="w-full text-left flex items-center justify-between gap-3 group"
                data-testid={testId ? `${testId}-toggle` : undefined}
            >
                <div className="eyebrow">{title}</div>
                <span className="text-xs text-[hsl(var(--mute))] group-hover:text-[hsl(var(--ink))]">{open ? "−" : "+"}</span>
            </button>
            {open && <div className="mt-2">{children}</div>}
        </section>
    );
};

const Para = ({ text }) => (hasContent(text) ? <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{text}</p> : null);

const Chips = ({ items, tone = "muted", testId, max = 12 }) => {
    const list = Array.isArray(items) ? items.filter((s) => hasContent(s)) : [];
    if (!list.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={testId}>
            {list.slice(0, max).map((s, i) => <Badge key={i} tone={tone}>{s}</Badge>)}
            {list.length > max && <span className="text-[11px] text-[hsl(var(--mute))]">+{list.length - max} more</span>}
        </div>
    );
};

const _humanize = (raw) => {
    if (!hasContent(raw)) return null;
    return String(raw).replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase());
};

const DriverRow = ({ driver, kind }) => {
    if (!driver || typeof driver !== "object") return null;
    const label = driver.driver || driver.name || driver.label;
    const impact = typeof driver.impact === "number" ? driver.impact : null;
    const impactLabel = driver.impactLabel || (impact != null ? `${impact >= 0 ? "+" : ""}${impact}` : null);
    const category = _humanize(driver.category);
    const isPenalty = kind === "penalty" || (impact != null && impact < 0)
        || (typeof driver.direction === "string" && driver.direction.toUpperCase() === "NEGATIVE");
    if (!hasContent(label) && !impactLabel && !category) return null;
    return (
        <li
            className="flex items-start gap-2.5 py-1.5"
            data-testid={`driver-row-${isPenalty ? "penalty" : "positive"}`}
        >
            {impactLabel && (
                <span
                    className={`shrink-0 inline-flex items-center justify-center min-w-[36px] px-1.5 py-0.5 rounded text-[11px] font-semibold num tracking-tight ${isPenalty
                        ? "text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/12 border border-[hsl(var(--warning))]/30"
                        : "text-[hsl(var(--positive))] bg-[hsl(var(--positive))]/10 border border-[hsl(var(--positive))]/25"}`}
                >
                    {impactLabel}
                </span>
            )}
            <div className="min-w-0 flex-1">
                <div className="text-[12px] text-[hsl(var(--ink))] leading-snug">{hasContent(label) ? label : "—"}</div>
                {(category || hasContent(driver.raw)) && (
                    <div className="text-[10px] text-[hsl(var(--mute))] mt-0.5 leading-snug">
                        {category}
                        {category && hasContent(driver.raw) && <span className="mx-1.5">·</span>}
                        {hasContent(driver.raw) && <span className="text-[hsl(var(--ink-soft))]">{driver.raw}</span>}
                    </div>
                )}
            </div>
        </li>
    );
};

const PartnerCard = ({ partner, alsoCompetitorInfo }) => {
    if (!partner) return null;
    const name = partner.name;
    if (!hasContent(name)) return null;
    const isOverlap = alsoCompetitorInfo && alsoCompetitorInfo.match;
    return (
        <article
            className={`border rounded p-3 ${isOverlap ? "border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/8" : "border-[hsl(var(--line))] bg-[hsl(var(--surface))]"}`}
            data-testid={`partner-card${isOverlap ? "-overlap" : ""}`}
        >
            <header className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-[hsl(var(--ink))]">{name}</span>
                {hasContent(partner.companyType) && <Badge tone="muted">{partner.companyType}</Badge>}
                {isOverlap && (
                    <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border border-[hsl(var(--warning))] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]"
                        title={alsoCompetitorInfo.via ? `Also listed under Relevant competitors as "${alsoCompetitorInfo.via}"` : "Flagged as also a competitor by the workflow"}
                        data-testid="partner-also-competitor"
                    >
                        Also a competitor
                    </span>
                )}
            </header>
            {hasContent(partner.rationale) && <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-1.5">{partner.rationale}</p>}
            {hasContent(partner.competitiveTension) && isOverlap && (
                <p className="text-[11px] text-[hsl(var(--warning))] leading-relaxed mt-1.5">
                    <span className="text-[10px] uppercase tracking-wider font-medium mr-1">Competitive tension</span>
                    {partner.competitiveTension}
                </p>
            )}
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1.5 text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">
                {hasContent(partner.evidenceBasis) && (
                    <span>Evidence: {partner.evidenceBasis.replace(/_/g, " ").toLowerCase()}</span>
                )}
                {hasContent(partner.recommendedEngagementModel) && (
                    <span>Engagement: {engagementLabel(partner.recommendedEngagementModel)?.toLowerCase()}</span>
                )}
            </div>
        </article>
    );
};

const BlindSpotItem = ({ entry, onSelectTrack, validTrackIds, trackLabelById }) => {
    if (!entry || (!hasContent(entry.title) && !hasContent(entry.summary))) return null;
    const sev = (entry.severity || "").toUpperCase();
    const sevTone = SEVERITY_TONE[sev] || null;
    const related = relatedTrackIdsOf(entry);
    // Only render an actionable button for IDs that exist in the current
    // client's trackCards (so the click is guaranteed to land on a card).
    // IDs that reference signals outside the current view are dropped from
    // BOTH the count and the chip list so the visible count always matches
    // the visible chips.
    const clickable = (validTrackIds && onSelectTrack)
        ? related.filter((tid) => validTrackIds.has(tid))
        : [];
    // Resolve each valid trackId to a reader-friendly label using the same
    // mapping the Client Signal Board uses (stableTrackName → signalName →
    // latestWeeklyFraming → raw id as last resort).
    const resolveLabel = (tid) => {
        const fromMap = trackLabelById && trackLabelById[tid];
        if (typeof fromMap === "string" && fromMap.trim()) return fromMap;
        return tid;
    };
    return (
        <li className="card-sunken p-2.5" data-testid="blind-spot-item">
            <div className="flex items-center gap-1.5 flex-wrap">
                {sev && sevTone && <Badge tone={sevTone}>{sev}</Badge>}
                {hasContent(entry.title) && <span className="text-xs font-semibold text-[hsl(var(--ink))] leading-snug">{entry.title}</span>}
            </div>
            {hasContent(entry.summary) && <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-1">{entry.summary}</p>}
            {hasContent(entry.whyItMatters) && (
                <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-1.5 border-l-2 border-[hsl(var(--accent))] pl-2 italic">{entry.whyItMatters}</p>
            )}
            {hasContent(entry.recommendedAction) && (
                <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-1.5">
                    <span className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium mr-1">Suggested step</span>
                    {entry.recommendedAction}
                </p>
            )}
            {clickable.length > 0 && (
                // The count reflects the number of *visible* clickable chips;
                // IDs filtered out by `validTrackIds` no longer contribute to
                // either the count or the chip list.
                <div className="flex items-center flex-wrap gap-1.5 mt-1.5" data-testid="blind-spot-linked-signals">
                    <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">
                        Linked to {clickable.length} {clickable.length === 1 ? "signal" : "signals"}
                    </span>
                    {clickable.map((tid) => (
                        <button
                            key={tid}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onSelectTrack(tid); }}
                            className="text-[10px] font-medium text-[hsl(var(--accent-ink))] hover:underline px-1.5 py-0.5 rounded border border-[hsl(var(--line))] hover:border-[hsl(var(--accent))] bg-[hsl(var(--surface))]"
                            data-testid={`blind-spot-linked-signal-${tid}`}
                            title={`Open signal ${tid}`}
                        >
                            {resolveLabel(tid)}
                        </button>
                    ))}
                </div>
            )}
        </li>
    );
};

const ClientTrackDetailPanel = ({ card, onClose, onSelectTrack, validTrackIds, trackLabelById }) => {
    const [showAllCompanies, setShowAllCompanies] = useState(false);
    const [showAllUrls, setShowAllUrls] = useState(false);
    const [showAllCompanyTypes, setShowAllCompanyTypes] = useState(false);
    const [showDrivers, setShowDrivers] = useState(false);

    if (!card) {
        return (
            <div className="card-flat p-6 text-sm text-[hsl(var(--mute))]" data-testid="client-track-detail-empty">
                Pick a signal to see the detailed client view.
            </div>
        );
    }

    const cat = (card?.client?.cardCategory || "").toUpperCase();
    const tone = CATEGORY_TONE[cat] || "muted";
    const evidenceUrls = extractEvidenceUrls(card?.evidence);
    const partners = Array.isArray(card?.evidence?.potentialPartners) ? card.evidence.potentialPartners : [];
    const existingRels = Array.isArray(card?.evidence?.existingRelationshipsToLeverage) ? card.evidence.existingRelationshipsToLeverage : [];
    const competitors = Array.isArray(card?.evidence?.relevantCompetitors) ? card.evidence.relevantCompetitors : [];
    const companies = Array.isArray(card?.evidence?.companies) ? card.evidence.companies : [];
    const companyTypes = Array.isArray(card?.evidence?.companyTypes) ? card.evidence.companyTypes : [];
    const hasPartnerSection = partners.length || existingRels.length || competitors.length;

    // Market signal context (compact stats + plain summary)
    const ctx = readMarketSignalContext(card);
    const hasMarketContextBlock = !!(ctx && (ctx.trackThesis || ctx.primaryUseCase || ctx.primaryMechanism || ctx.macroTheme || ctx.currentMarketMovement));

    // Client-relevance explanation (new v1.1 fields, with v1 fallback to whyRelevantToClient).
    const rel = readClientRelevance(card);
    const relSummary = rel.relevanceFrontendSummary;
    // Suppress the rationale paragraph when it essentially repeats the summary
    // (exact match, containment, or ≥80% Jaccard token overlap). See
    // `isDuplicateRelevanceText` — normalised comparison strips punctuation /
    // case / stopwords / short tokens so near-duplicates also collapse.
    const relRationale = (
        rel.clientRelevanceRationale
        && !isDuplicateRelevanceText(relSummary, rel.clientRelevanceRationale)
    ) ? rel.clientRelevanceRationale : null;
    const hasWhyBlock = !!(relSummary || relRationale || rel.selectionRationale);
    const hasDriversBlock = rel.positiveDrivers.length > 0 || rel.penalties.length > 0;
    const hasMetadataLine = !!(rel.candidateSelectionMode
        || rel.hasCurrentDynamics != null
        || rel.deterministicCandidateScore != null
        || rel.scorePenalty != null);

    // Opportunity / Threat text (read from interpretation first, with fallbacks).
    const opportunityText = (() => {
        const i = card?.interpretation || {};
        if (hasContent(i.opportunity)) return i.opportunity;
        if (hasContent(card?.opportunity)) return card.opportunity;
        if (hasContent(card?.client?.opportunity)) return card.client.opportunity;
        return null;
    })();
    const threatText = (() => {
        const i = card?.interpretation || {};
        if (hasContent(i.threat)) return i.threat;
        if (hasContent(card?.threat)) return card.threat;
        if (hasContent(card?.client?.threat)) return card.client.threat;
        return null;
    })();

    // Per-card blind-spot split (deduped). Falls back to legacy single blindSpot string.
    const cardBlindGroups = buildBlindSpotGroups({
        profileLevelBlindSpots: card.profileLevelBlindSpots,
        signalLinkedBlindSpots: card.signalLinkedBlindSpots,
        structuredBlindSpots: card.structuredBlindSpots,
        legacyTopBlindSpots: Array.isArray(card.blindSpots) ? card.blindSpots : null,
    });
    const hasStructuredBlindSpots = cardBlindGroups.profile.length > 0 || cardBlindGroups.signalLinked.length > 0;

    return (
        <aside className="card-flat p-6 space-y-2" data-testid="client-track-detail-panel">
            <header className="flex items-start justify-between gap-3">
                <div>
                    <div className="eyebrow">Selected signal</div>
                    <h3 className="display text-xl mt-1 leading-snug">{card.stableTrackName || card.trackId}</h3>
                </div>
                {onClose && (
                    <button type="button" onClick={onClose} className="text-[hsl(var(--mute))] hover:text-[hsl(var(--ink))] text-lg leading-none" aria-label="Close">×</button>
                )}
            </header>

            <div className="flex flex-wrap items-center gap-2">
                {cat && <Badge tone={tone}>{CATEGORY_LABEL[cat] || card?.client?.cardCategory}</Badge>}
                {hasContent(card?.market?.momentum) && <Badge tone={marketMomentumTone(card.market.momentum)}>{marketMomentumLabel(card.market.momentum)}</Badge>}
                {hasContent(card?.market?.role) && <Badge tone="muted" data-testid="market-role-badge">{signalStatusLabel(card.market.role)}</Badge>}
                {hasContent(card?.market?.maturityStage) && <Badge tone="muted" data-testid="market-maturity-badge">{signalStatusLabel(card.market.maturityStage)}</Badge>}
                {hasContent(card?.market?.convictionLevel) && <Badge tone="muted">{card.market.convictionLevel} conviction</Badge>}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3">
                <Pill label="Client relevance" value={card?.client?.relevanceScore != null ? String(card.client.relevanceScore) : null} tone="ink" hidden={card?.client?.relevanceScore == null} />
                <Pill label="Urgency" value={levelLabel(card?.client?.urgency)} tone={levelTone(card?.client?.urgency)} hidden={!hasContent(card?.client?.urgency)} />
                <Pill label="Opportunity" value={levelLabel(card?.client?.opportunityLevel)} tone={levelTone(card?.client?.opportunityLevel)} hidden={!hasContent(card?.client?.opportunityLevel)} />
                <Pill label="Threat" value={levelLabel(card?.client?.threatLevel)} tone={levelTone(card?.client?.threatLevel)} hidden={!hasContent(card?.client?.threatLevel)} />
            </div>

            <Section title="Latest weekly framing" testId="section-latest-weekly-framing" hidden={!hasContent(card.latestWeeklyFraming)}>
                <Para text={card.latestWeeklyFraming} />
            </Section>

            {hasWhyBlock && (
                <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid="section-why-this-matters">
                    <div className="eyebrow mb-2">Why this signal matters</div>
                    {relSummary && <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed" data-testid="why-this-matters-summary">{relSummary}</p>}
                    {relRationale && (
                        <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2 border-l-2 border-[hsl(var(--accent))] pl-3 italic" data-testid="why-this-matters-rationale">
                            {relRationale}
                        </p>
                    )}
                    {hasContent(rel.selectionRationale) && (
                        <p className="text-[11px] text-[hsl(var(--ink-soft))] leading-relaxed mt-2" data-testid="why-selected">
                            <span className="text-[hsl(var(--mute))] uppercase tracking-wider text-[10px] font-medium mr-1.5">Why selected</span>
                            {rel.selectionRationale}
                        </p>
                    )}
                </section>
            )}

            {hasDriversBlock && (
                <section className="border-t border-[hsl(var(--line))] pt-4 mt-4" data-testid="section-relevance-drivers">
                    <button
                        type="button"
                        onClick={() => setShowDrivers((v) => !v)}
                        aria-expanded={showDrivers}
                        data-testid="drivers-toggle"
                        className="w-full flex items-center justify-between text-left"
                    >
                        <div>
                            <div className="eyebrow">Relevance score drivers</div>
                            <div className="text-[11px] text-[hsl(var(--mute))] mt-0.5">
                                {rel.positiveDrivers.length} positive{rel.penalties.length ? ` · ${rel.penalties.length} penalt${rel.penalties.length === 1 ? "y" : "ies"}` : ""}
                            </div>
                        </div>
                        <span className="text-[hsl(var(--mute))] text-xs">{showDrivers ? "Hide" : "Show"}</span>
                    </button>
                    {showDrivers && (
                        <div className="mt-3 space-y-3">
                            {rel.positiveDrivers.length > 0 && (
                                <div data-testid="drivers-positive">
                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1">Top drivers</div>
                                    <ul className="divide-y divide-[hsl(var(--line))]">
                                        {rel.positiveDrivers.map((d, i) => <DriverRow key={`pd-${i}`} driver={d} kind="positive" />)}
                                    </ul>
                                </div>
                            )}
                            {rel.penalties.length > 0 && (
                                <div data-testid="drivers-penalties">
                                    <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1">Penalties</div>
                                    <ul className="divide-y divide-[hsl(var(--line))]">
                                        {rel.penalties.map((d, i) => <DriverRow key={`pn-${i}`} driver={d} kind="penalty" />)}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </section>
            )}

            {hasMetadataLine && (
                <section className="border-t border-[hsl(var(--line))] pt-2 mt-3" data-testid="section-relevance-meta">
                    <div className="text-[10px] text-[hsl(var(--mute))] leading-relaxed">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {rel.candidateSelectionMode && (
                                <span>Selection: <span className="text-[hsl(var(--ink-soft))]">{humanizeEnumLabel(rel.candidateSelectionMode, "selectionMode")}</span></span>
                            )}
                            {rel.hasCurrentDynamics != null && (
                                <>
                                    {rel.candidateSelectionMode && <span aria-hidden>·</span>}
                                    <span>Current dynamics: <span className="text-[hsl(var(--ink-soft))]">{rel.hasCurrentDynamics ? "Yes" : "No"}</span></span>
                                </>
                            )}
                        </div>
                        {(rel.deterministicCandidateScore != null || (rel.scorePenalty != null && rel.scorePenalty !== 0)) && (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                                {rel.deterministicCandidateScore != null && (
                                    <span>Candidate score: <span className="text-[hsl(var(--ink-soft))] num">{rel.deterministicCandidateScore}</span></span>
                                )}
                                {rel.scorePenalty != null && rel.scorePenalty !== 0 && (
                                    <>
                                        <span aria-hidden>·</span>
                                        <span>Penalty: <span className="text-[hsl(var(--warning))] num">{rel.scorePenalty}</span></span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </section>
            )}

            <Section title="Opportunity" testId="section-opportunity" hidden={!opportunityText} collapsible>
                <Para text={opportunityText} />
            </Section>
            <Section title="Threat" testId="section-threat" hidden={!threatText} collapsible>
                <Para text={threatText} />
            </Section>

            <Section title="Market signal context" testId="section-market-signal-context" hidden={!hasMarketContextBlock} collapsible>
                <div className="flex flex-wrap items-center gap-1.5 mb-2" data-testid="market-context-chips">
                    {typeof card?.market?.score === "number" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[hsl(var(--line))] bg-[hsl(var(--surface-raised))] text-[11px] text-[hsl(var(--ink-soft))]">
                            <span className="text-[hsl(var(--mute))] uppercase tracking-wider text-[9px] font-medium">Score</span>
                            <span className="font-semibold num text-[hsl(var(--ink))]">{card.market.score}</span>
                        </span>
                    )}
                    {hasContent(card?.market?.momentum) && (
                        <Badge tone={marketMomentumTone(card.market.momentum)}>{marketMomentumLabel(card.market.momentum)}</Badge>
                    )}
                    {hasContent(ctx.macroTheme) && <Badge tone="muted">{ctx.macroTheme}</Badge>}
                    {hasContent(ctx.primaryUseCase) && <Badge tone="muted">{ctx.primaryUseCase}</Badge>}
                    {hasContent(ctx.primaryMechanism) && <Badge tone="muted">{ctx.primaryMechanism}</Badge>}
                </div>
                {hasContent(ctx.trackThesis) && (
                    <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed" data-testid="market-context-thesis">{ctx.trackThesis}</p>
                )}
                {hasContent(ctx.currentMarketMovement) && (
                    <p className="text-xs text-[hsl(var(--ink-soft))] leading-relaxed mt-2 border-l-2 border-[hsl(var(--accent))] pl-3 italic" data-testid="market-context-movement">{ctx.currentMarketMovement}</p>
                )}
            </Section>

            <Section title="Why this is relevant" testId="section-why-relevant" hidden={!hasContent(card?.client?.whyRelevantToClient) || hasWhyBlock} collapsible>
                <Para text={card?.client?.whyRelevantToClient} />
            </Section>
            <Section title="Current market movement" testId="section-current-market-movement" hidden={!hasContent(card?.market?.weeklyComparisonNarrative || card?.market?.evolution) || hasMarketContextBlock} collapsible>
                <Para text={card?.market?.weeklyComparisonNarrative || card?.market?.evolution} />
            </Section>

            {hasStructuredBlindSpots ? (
                <Section title="Visibility gaps" testId="section-blind-spots" collapsible>
                    {cardBlindGroups.profile.length > 0 && (
                        <div className="mb-3" data-testid="blind-spots-profile-rail">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Profile-level · capability or governance gaps</div>
                            <ul className="space-y-2">
                                {cardBlindGroups.profile.map((b, i) => <BlindSpotItem key={`p-${i}`} entry={b} onSelectTrack={onSelectTrack} validTrackIds={validTrackIds} trackLabelById={trackLabelById} />)}
                            </ul>
                        </div>
                    )}
                    {cardBlindGroups.signalLinked.length > 0 && (
                        <div data-testid="blind-spots-signal-linked-rail">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium mb-1.5">Signal-linked · tied to this market signal</div>
                            <ul className="space-y-2">
                                {cardBlindGroups.signalLinked.map((b, i) => <BlindSpotItem key={`s-${i}`} entry={b} onSelectTrack={onSelectTrack} validTrackIds={validTrackIds} trackLabelById={trackLabelById} />)}
                            </ul>
                        </div>
                    )}
                </Section>
            ) : (
                <Section title="Visibility gap" testId="section-blind-spot" hidden={!hasContent(card?.client?.blindSpot)} collapsible>
                    <Para text={card?.client?.blindSpot} />
                </Section>
            )}

            <Section title="Recommended next action" testId="section-recommended-action" hidden={!hasContent(card?.client?.recommendedNextAction || card?.client?.suggestedAction)} collapsible>
                <Para text={card?.client?.recommendedNextAction || card?.client?.suggestedAction} />
            </Section>

            <Section title="Affected business segments" testId="section-affected-business-segments" hidden={!hasContent(card.affectedBusinessSegments)} collapsible>
                <Chips items={card.affectedBusinessSegments} tone="muted" testId="chips-business-segments" />
            </Section>
            <Section title="Affected capability areas" testId="section-affected-capability-areas" hidden={!hasContent(card.affectedCapabilityAreas)} collapsible>
                <Chips items={card.affectedCapabilityAreas} tone="muted" testId="chips-capability-areas" />
            </Section>

            {hasPartnerSection && (
                <Section title="Partner and competitive context" testId="section-partner-context" collapsible>
                    {partners.length > 0 && (
                        <div data-testid="subsection-potential-partners">
                            <div className="flex items-baseline justify-between gap-2 mt-1 mb-2">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Potential partners</div>
                                <span className="text-[10px] text-[hsl(var(--mute))] num">{partners.length}</span>
                            </div>
                            <ul className="grid grid-cols-1 gap-2">
                                {partners.map((p, i) => {
                                    const overlap = isPartnerAlsoCompetitor(p, competitors);
                                    return <li key={p.name || i}><PartnerCard partner={p} alsoCompetitorInfo={overlap} /></li>;
                                })}
                            </ul>
                        </div>
                    )}
                    {existingRels.length > 0 && (
                        <div className="mt-3" data-testid="subsection-existing-relationships">
                            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Existing relationships to leverage</div>
                                <span className="text-[10px] text-[hsl(var(--mute))] num">{existingRels.length}</span>
                            </div>
                            <Chips items={existingRels} tone="positive" testId="chips-existing-relationships" max={20} />
                        </div>
                    )}
                    {competitors.length > 0 && (
                        <div className="mt-3" data-testid="subsection-relevant-competitors">
                            <div className="flex items-baseline justify-between gap-2 mb-1.5">
                                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Relevant competitors</div>
                                <span className="text-[10px] text-[hsl(var(--mute))] num">{competitors.length}</span>
                            </div>
                            <Chips items={competitors} tone="warning" testId="chips-relevant-competitors" max={20} />
                        </div>
                    )}
                </Section>
            )}

            <Section title="Evidence" testId="section-evidence" hidden={!evidenceUrls.length && !companies.length && !companyTypes.length}>
                {evidenceUrls.length > 0 && (
                    <div className="mb-3" data-testid="evidence-urls-block">
                        <div className="flex items-baseline justify-between gap-2 mb-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Source URLs</div>
                            <span className="text-[10px] text-[hsl(var(--mute))] num">{showAllUrls ? evidenceUrls.length : Math.min(8, evidenceUrls.length)} / {evidenceUrls.length}</span>
                        </div>
                        <ul className="space-y-1.5" data-testid="evidence-urls">
                            {(showAllUrls ? evidenceUrls : evidenceUrls.slice(0, 8)).map((s, i) => (
                                <li key={i} className="text-xs leading-snug">
                                    <a href={s.url} target="_blank" rel="noreferrer" className="text-[hsl(var(--accent-ink))] hover:underline" title={s.url}>{s.label}</a>
                                </li>
                            ))}
                        </ul>
                        {evidenceUrls.length > 8 && (
                            <button
                                type="button"
                                onClick={() => setShowAllUrls((v) => !v)}
                                data-testid="toggle-all-urls"
                                className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1.5"
                            >
                                {showAllUrls ? "Show fewer" : `Show all ${evidenceUrls.length} URLs`}
                            </button>
                        )}
                    </div>
                )}
                {companyTypes.length > 0 && (
                    <div className="mb-3" data-testid="evidence-company-types">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Company types</div>
                            <span className="text-[10px] text-[hsl(var(--mute))] num">{companyTypes.length}</span>
                        </div>
                        <Chips items={companyTypes} tone="muted" max={showAllCompanyTypes ? 500 : 12} />
                        {companyTypes.length > 12 && (
                            <button
                                type="button"
                                onClick={() => setShowAllCompanyTypes((v) => !v)}
                                data-testid="toggle-all-company-types"
                                className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1.5"
                            >
                                {showAllCompanyTypes ? "Show fewer" : `Show all ${companyTypes.length}`}
                            </button>
                        )}
                    </div>
                )}
                {companies.length > 0 && (
                    <div data-testid="evidence-companies">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--mute))] font-medium">Companies cited</div>
                            <span className="text-[10px] text-[hsl(var(--mute))] num">{showAllCompanies ? companies.length : Math.min(12, companies.length)} / {companies.length}</span>
                        </div>
                        <Chips items={companies} tone="muted" max={showAllCompanies ? 500 : 12} />
                        {companies.length > 12 && (
                            <button
                                type="button"
                                onClick={() => setShowAllCompanies((v) => !v)}
                                data-testid="toggle-all-companies"
                                className="text-[11px] font-medium text-[hsl(var(--accent-ink))] hover:underline mt-1.5"
                            >
                                {showAllCompanies ? "Show fewer" : `Show all ${companies.length}`}
                            </button>
                        )}
                    </div>
                )}
            </Section>
        </aside>
    );
};

export default ClientTrackDetailPanel;
